import { useMutation, useQuery } from '@tanstack/react-query';

import reviewQuestionService from './reviewQuestionService';

import type { QuestionComment } from '@/components/QuestionResultContainer/types';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { queryClient } from '@/AppWithProviders';
import {
    CommentReplyQueryKey,
    CycleEvaluationQueryKey,
    CycleQuestionnaireQueryKey,
    ReviewQueryKey,
    ReviewQuestionMutationKey,
    ReviewTemplateQueryKey,
} from '@/constants/QueryKeys';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { showErrorToast } from '@/plugins/Toast';

export const useCreateReviewQuestion = () => {
    return useMutation({
        mutationKey: [ReviewQuestionMutationKey.REVIEW_QUESTION],
        mutationFn: (reviewQuestion: ReviewQuestion) =>
            reviewQuestionService.createReviewQuestion(reviewQuestion),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
            queryClient.invalidateQueries({
                queryKey: [CycleQuestionnaireQueryKey.CYCLE_QUESTIONNAIRE],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE],
            });
        },
    });
};

export const useUpdateReviewQuestion = () => {
    return useMutation({
        mutationKey: [ReviewQuestionMutationKey.REVIEW_QUESTION],
        mutationFn: (reviewQuestion: ReviewQuestion) =>
            reviewQuestionService.updateReviewQuestion(reviewQuestion),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
            queryClient.invalidateQueries({
                queryKey: [CycleQuestionnaireQueryKey.CYCLE_QUESTIONNAIRE],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE],
            });
            queryClient.invalidateQueries({
                queryKey: [CycleEvaluationQueryKey.CYCLE_EVALUATION],
            });
        },
    });
};

export const useDeleteReviewQuestion = () => {
    return useMutation({
        mutationKey: [ReviewQuestionMutationKey.REVIEW_QUESTION],
        mutationFn: (reviewQuestionId: string) =>
            reviewQuestionService.deleteReviewQuestion(reviewQuestionId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
            queryClient.invalidateQueries({
                queryKey: [CycleQuestionnaireQueryKey.CYCLE_QUESTIONNAIRE],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE],
            });
        },
    });
};

export const useQuestionComments = (questionId: string) => {
    const { t } = useAmelioTranslations();

    return useQuery<QuestionComment[]>({
        queryKey: [CommentReplyQueryKey.COMMENT_REPLIES, questionId],
        queryFn: async () => {
            try {
                return await reviewQuestionService.getQuestionComments(questionId);
            } catch (error) {
                showErrorToast({
                    title: t('common.error'),
                    message: t('questionResult.errors.fetchCommentsFailed'),
                });
                throw error;
            }
        },
        enabled: !!questionId,
    });
};
